/**
 *  Component: Link Team with Channel
 */
import React, { useState, useEffect } from 'react';

import { Grid } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import LinkIcon from '@material-ui/icons/Link';
import Skeleton from '@material-ui/lab/Skeleton';
import TeamChannelMapping from 'models/admin/teamChannelMapping';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Button, MeshSelect } from 'styles/admin';
import { StyledIconButton, StyledTypography, StyledTooltip } from 'styles/common';

import emptyStateImage from 'assets/images/emptyState.png';

import { SEARCH_TAG_TYPES, SLACK_PRIVATE_CHANNEL_INFO_TEXT } from 'constants/ProjectConstants';
import theme from 'constants/theme';

import {
  getChannels,
  getTeamChannelMappings,
  saveTeamChannelMapping,
  updateTeamChannelMapping,
  deleteTeamChannelMapping,
  saveSlackApplauseMapping,
  getSlackApplauseDetails,
  updateSlackApplauseChannelMapping,
} from 'services/plugins';
import { universalSearchAutoCompleteV1 } from 'services/search';
import { showErrorSnackbar } from 'services/snackbar';

import { BodyContainer, FormContainer, FormHeading, Separator, Icon } from '../styles';
import SlackMappingFields from './slackMappingFields';

const thirdPartyType = 'slack';

const SlackPlugin = ({ isActivated = false }) => {
  const dispatch = useDispatch();
  const [dataLoading, setDataLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [newMapping, setNewMapping] = useState(null);
  const [teams, setTeams] = useState([]);
  const [applauseFieldStatus, setapplauseFieldStatus] = useState([]);

  const showApplauseModule = true;

  const getMappings = () => {
    setDataLoading(true);
    getTeamChannelMappings({ thirdPartyType })
      .then((mappings) => {
        setMappings(mappings);
      })
      .catch((err) => dispatch(showErrorSnackbar('Sorry, unable to fetch the mappings')))
      .finally(() => setDataLoading(false));
  };

  const getAllChannels = () => {
    setDataLoading(true);
    getChannels({ thirdPartyType })
      .then((res) => {
        if (res.length) {
          setChannels(res.map((channel) => ({ value: channel.id, label: channel.name })));
          getMappings();
        } else {
          dispatch(showErrorSnackbar('No channels were found'));
          setDataLoading(false);
        }
      })
      .catch((err) => {
        dispatch(showErrorSnackbar('Sorry, unable to fetch the channels!'));
        setDataLoading(false);
      });
  };

  const getTeams = () => {
    const tagsSet = [
      {
        tagType: 'TEAM',
        limit: 50,
      },
    ];
    universalSearchAutoCompleteV1({ text: '', tagsSet, callback: searchCallBack });
  };

  const searchCallBack = (res) => {
    const teamsResult = res ? res.filter((r) => r.label === SEARCH_TAG_TYPES.team) : [];
    setDataLoading(false);
    if (teamsResult.length) {
      isActivated && getAllChannels();
      setTeams(teamsResult[0].options.map((r) => ({ label: r.label, value: r.id })));
    } else {
      dispatch(showErrorSnackbar('No teams were found'));
    }
  };

  const getSlackApplause = () => {
    setDataLoading(true);
    getSlackApplauseDetails()
      .then((res) => {
        setapplauseFieldStatus(
          res.map((channel) => ({
            value: channel.thirdPartyTeamId,
            label: channel.thirdPartyTeamName,
            uuid: channel.uuid,
          }))
        );
      })
      .catch((err) => dispatch(showErrorSnackbar('Sorry, unable to fetch the mappings!')))
      .finally(() => setDataLoading(false));
  };

  useEffect(() => {
    setDataLoading(true);
    getTeams();
    getSlackApplause();
  }, []);

  const getThirdPartyTeamName = (id) => channels.filter((channel) => channel.value === id)[0].label;

  const saveApplauseMapping = (value, label) => {
    saveSlackApplauseMapping({ thirdPartyTeamId: value, thirdPartyTeamName: label })
      .then((res) => {
        getSlackApplause();
      })
      .catch((err) => dispatch(showErrorSnackbar('Sorry, unable to save the mapping!')));
  };

  const updateApplauseMapping = (value, label, mappingId) => {
    updateSlackApplauseChannelMapping({
      thirdPartyTeamId: value,
      thirdPartyTeamName: label,
      mappingId,
    })
      .then((res) => {
        getSlackApplause();
      })
      .catch((err) => dispatch(showErrorSnackbar('Sorry, unable to save the mapping!')));
  };

  const createMapping = (value, key) => {
    if (key === 'team') {
      if (newMapping.channel) {
        setLoading(true);
        saveTeamChannelMapping({
          thirdPartyType,
          thirdPartyTeamId: newMapping.thirdPartyTeamId,
          thirdPartyTeamName: getThirdPartyTeamName(newMapping.thirdPartyTeamId),
          teamId: value,
        })
          .then((res) => {
            getMappings();
          })
          .catch((err) => dispatch(showErrorSnackbar('Sorry, unable to save the mapping!')))
          .finally(() => {
            setNewMapping(null);
            setLoading(false);
          });
      } else {
        setNewMapping({ ...newMapping, teamId: value });
      }
    } else if (newMapping.teamId) {
      setLoading(true);
      saveTeamChannelMapping({
        thirdPartyType,
        thirdPartyTeamId: value,
        thirdPartyTeamName: getThirdPartyTeamName(value),
        teamId: newMapping.teamId,
      })
        .then((res) => {
          getMappings();
        })
        .catch((err) => dispatch(showErrorSnackbar('Sorry, unable to save the mapping!')))
        .finally(() => {
          setNewMapping(null);
          setLoading(false);
        });
    } else {
      setNewMapping({ ...newMapping, thirdPartyTeamId: value });
    }
  };

  const updateMapping = (value, key, mapping) => {
    if (key === 'team') {
      setLoading(true);
      updateTeamChannelMapping({
        ...mapping,
        thirdPartyType,
        teamId: value,
      })
        .then((res) => {
          getMappings();
        })
        .catch((err) => {
          dispatch(showErrorSnackbar('Sorry, unable to update the mapping!'));
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      updateTeamChannelMapping({
        ...mapping,
        thirdPartyType,
        thirdPartyTeamId: value,
        thirdPartyTeamName: getThirdPartyTeamName(value),
      })
        .then((res) => {
          getMappings();
        })
        .catch((err) => {
          dispatch(showErrorSnackbar('Sorry, unable to update the mapping!'));
          setLoading(false);
        })
        .finally(() => setLoading(false));
    }
  };

  const onButtonClick = () => setNewMapping(new TeamChannelMapping());

  const deleteMapping = (mappingId) => {
    setLoading(true);
    deleteTeamChannelMapping({ thirdPartyType, mappingId })
      .then((res) => {
        getMappings();
      })
      .then(() => getSlackApplause())
      .catch((err) => {
        dispatch(showErrorSnackbar('Sorry, unable to delete the mapping!'));
      })
      .finally(() => setLoading(false));
  };

  const removeNewMapping = () => setNewMapping(null);

  return (
    <FormContainer>
      <FormHeading>
        <Icon>
          <LinkIcon />
        </Icon>
        Link slack channels for pod updates{' '}
      </FormHeading>
      {/* {showApplauseModule &&
          (applauseFieldStatus.length ? (
            <ApplauseContainer
              channels={channels}
              loading={loading}
              updateFn={updateApplauseMapping}
              deleteFn={deleteMapping}
              value={applauseFieldStatus}
            />
          ) : (
            <ApplauseContainer channels={channels} loading={loading} saveFn={saveApplauseMapping} />
          ))} */}
      {(!mappings || !mappings.length) && !newMapping && !loading && !dataLoading ? (
        <>
          <Image alt="link" src={emptyStateImage} onClick={onButtonClick} />
          <StyledTypography weight={600} variant="h5" align="center" color={theme.palette.text.disabled}>
            Click Above to Add more Channels
          </StyledTypography>
        </>
      ) : (
        <>
          <HeaderContainer>
            <Grid container>
              <Grid item xs={5}>
                <StyledTypography weight={600} variant="body2" color={theme.palette.text.disabled}>
                  Team name
                </StyledTypography>
              </Grid>
              <Grid item xs={2} />
              <Grid item xs={5}>
                <StyledTypography weight={600} variant="body2" color={theme.palette.text.disabled}>
                  Slack channel
                  <StyledTooltip title={SLACK_PRIVATE_CHANNEL_INFO_TEXT}>
                    <StyledInfoIcon />
                  </StyledTooltip>
                </StyledTypography>
              </Grid>
            </Grid>
          </HeaderContainer>
          <Separator />

          <BodyContainer>
            {dataLoading ? (
              <Grid container>
                <Grid item xs={5}>
                  <Skeleton height="30px" />
                  <Skeleton height="30px" />
                </Grid>
                <Grid item xs={2} />
                <Grid item xs={5}>
                  <Skeleton height="30px" width="80%" />
                  <Skeleton height="30px" width="80%" />
                </Grid>
              </Grid>
            ) : (
              <>
                {mappings.map((mapping) => (
                  <SlackMappingFields
                    loading={loading}
                    saveFn={updateMapping}
                    deleteFn={deleteMapping}
                    teams={teams}
                    channels={channels}
                    mapping={mapping}
                  />
                ))}
              </>
            )}

            {
              // new
              newMapping && (
                <SlackMappingFields
                  loading={loading}
                  saveFn={createMapping}
                  deleteFn={removeNewMapping}
                  teams={teams}
                  channels={channels}
                  mapping={newMapping}
                />
              )
            }
            {!newMapping && (
              <NewButton compact size="large" topGutter={4} onClick={onButtonClick} disabled={loading}>
                <StyledTypography cursor="pointer" weight={600} variant="body2" color={theme.palette.text.disabled}>
                  Link more Mesh teams with Slack channel
                </StyledTypography>
              </NewButton>
            )}
          </BodyContainer>
        </>
      )}
    </FormContainer>
  );
};

export const ApplauseContainer = ({ saveFn, updateFn, deleteFn, channels, loading, value }) => (
  <>
    <Separator style={{ marginLeft: '-1.6rem', marginRight: '-1.5rem' }} />
    <Grid container alignItems="center" justify="start">
      <Grid style={{ marginRight: '8px' }}>
        <StyledTypography align="left" variant="body1">
          Feedback Channel
        </StyledTypography>
      </Grid>
      <Grid item xs={5} style={{ marginTop: '8px' }}>
        {saveFn ? (
          <StyledMeshSelect
            placeholder="select channel"
            options={channels}
            isDisabled={loading}
            onChange={(data) => saveFn(data.value, data.label)}
          />
        ) : (
          <>
            <StyledMeshSelect
              placeholder="select channel"
              value={value}
              options={channels}
              isDisabled={loading}
              onChange={(data) =>
                updateFn(
                  data.value,
                  data.label,
                  value.map((res) => res.uuid)
                )
              }
            />
            {/* uncomment the below lines if delete delete button is required in future for deleteing the slack applause mapping */}
            {/* <Grid item xs={1}>
              <IconButton>
                <DeleteIcon onClick={() => deleteFn(value.map(res => res.uuid))} />
              </IconButton>
            </Grid> */}
          </>
        )}
      </Grid>
    </Grid>
    <Separator style={{ marginLeft: '-1.6rem', marginRight: '-1.5rem' }} />
  </>
);

const HeaderContainer = styled.div`
  padding: 0 1.5rem;
`;

const NewButton = styled(Button)`
  border: 1px dashed #eaeaea;
  width: 100%;
  padding: 0.75rem;
  margin-top: 1rem;
`;

const Image = styled.img`
  cursor: pointer;
  margin: 0 auto;
  display: table;
`;

const StyledInfoIcon = styled(InfoIcon)`
  color: rgb(136, 136, 136);
  margin-left: 0.5rem;
  vertical-align: text-bottom;
  font-size: 1rem;
  cursor: pointer;
`;
const IconButton = styled(StyledIconButton)`
  display: table;
  margin: 14px auto 25px auto;
`;

const StyledMeshSelect = styled(MeshSelect)`
  z-index: 999;
`;

export default SlackPlugin;
